import { ReactNode, useRef, useState } from 'react'

import { useTranslation } from 'next-i18next'

import Tooltip from '~/components/Tooltip'
import TimesBoldIcon from '~/components/Icons/svgs/TimesBoldIcon'
import { isTouchDevice } from '~/utils'
import useClickOutside from '~/shared/hooks/useClickOutside.hook'
import infrastructureAnalytics from '~/analytics/infrastructure/infrastructureAnalytics'
import SVGIcon from '~/components/Icons/svgs/SVGIcon'

import ExclamationCircleStrokeIcon from '../Icons/svgs/ExclamationCircleStrokeIcon'

import { ArrowPosition, TooltipWidth } from './Tooltip.types'

export default function MenuTooltip({
  children,
  className = '',
  arrowPosition = 'bottom-right',
  width = 'normal',
  itemLabel,
}: {
  children: ReactNode
  className?: string
  arrowPosition?: ArrowPosition
  width?: TooltipWidth
  itemLabel: string
}): React.ReactElement {
  const { t } = useTranslation('generics')
  const [showTooltip, setShowTooltip] = useState(false)

  const handleOnClick = (): void => {
    const isTouchScreen = isTouchDevice()
    if (isTouchScreen) {
      if (!showTooltip) {
        infrastructureAnalytics.logViewMenuTooltip(itemLabel)
      }
      setShowTooltip((prevState) => !prevState)
    }
  }

  const handleOnMouseEnter = (): void => {
    const isTouchScreen = isTouchDevice()
    if (!isTouchScreen) {
      infrastructureAnalytics.logViewMenuTooltip(itemLabel)
      setShowTooltip(true)
    }
  }

  const handleOnMouseLeave = (): void => {
    const isTouchScreen = isTouchDevice()
    if (!isTouchScreen) {
      setShowTooltip(false)
    }
  }

  const ref = useRef<HTMLButtonElement>(null)
  useClickOutside(ref, () => setShowTooltip(false))

  return (
    <div className={`relative ${showTooltip ? `z-10` : ''}`}>
      <span className={`cursor-pointer ${className}`} ref={ref}>
        <Tooltip arrowPosition={arrowPosition} showBorder showTooltip={showTooltip} width={width}>
          {isTouchDevice() && (
            <button
              aria-label={t('close')}
              className="absolute scale-golden pl-4 pb-4 right-1 top-1"
              onClick={handleOnClick}
            >
              <SVGIcon icon={TimesBoldIcon} pathClassName="text-purple" size={18} />
            </button>
          )}
          {children}
        </Tooltip>
        <span onClick={handleOnClick} onMouseEnter={handleOnMouseEnter} onMouseLeave={handleOnMouseLeave}>
          <SVGIcon icon={ExclamationCircleStrokeIcon} pathClassName="text-purple" />
        </span>
      </span>
    </div>
  )
}
